import { useCallback } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop, useMutationInterop } from '@confluence/apollo-relay-interop';
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop';

import type { useContextualEmojiUserPreferencesQuery$data } from './__generated__/useContextualEmojiUserPreferencesQuery.graphql';
import type { useContextualEmojiUserPreferencesMutation } from './__generated__/useContextualEmojiUserPreferencesMutation.graphql';

const UserPreferencesFragment = graphql`
	fragment useContextualEmojiUserPreferencesFragment on UserPreferences {
		contextualEmojiOptOut
	}
`;

export const useContextualEmojiUserPreferences = () => {
	const { data } = useQueryInterop<useContextualEmojiUserPreferencesQuery$data>(
		graphql`
			query useContextualEmojiUserPreferencesQuery {
				userPreferences {
					contextualEmojiOptOut
				}
			}
		`,
		{
			fetchPolicy: 'cache-and-network',
		},
	);

	const [updateContextualEmojiOptOutServer] =
		useMutationInterop<useContextualEmojiUserPreferencesMutation>(graphql`
			mutation useContextualEmojiUserPreferencesMutation($contextualEmojiOptOut: Boolean!) {
				updateUserPreferences(userPreferences: { contextualEmojiOptOut: $contextualEmojiOptOut }) {
					contextualEmojiOptOut
					__typename
				}
			}
		`);

	const writeFragmentInterop = useWriteFragmentInterop<any>({
		fragmentSpec: UserPreferencesFragment,
	});

	const isContextualEmojiEnabled = !data?.userPreferences?.contextualEmojiOptOut;

	const updateContextualEmojiOptOut = useCallback(
		async (newContextualEmojiOptOut: boolean) => {
			writeFragmentInterop({
				fragmentId: '$ROOT_QUERY.userPreferences',
				data: {
					contextualEmojiOptOut: newContextualEmojiOptOut,
					__typename: 'UserPreferences',
				},
			});

			updateContextualEmojiOptOutServer({
				variables: { contextualEmojiOptOut: newContextualEmojiOptOut },
			}).catch(() => {
				writeFragmentInterop({
					fragmentId: '$ROOT_QUERY.userPreferences',
					data: {
						contextualEmojiOptOut: !newContextualEmojiOptOut,
						__typename: 'UserPreferences',
					},
				});
			});
		},
		[updateContextualEmojiOptOutServer, writeFragmentInterop],
	);

	return {
		isContextualEmojiEnabled,
		updateContextualEmojiOptOut,
	};
};
